import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import Potrace from "potrace";
import { finishApplyLoading } from "./stitchApplyLoading";
import {
  buildPotraceBitmapCanvas,
  collectLayerPathDs,
  isCanvasEffectivelyEmpty,
  PIXEL_ERASE_POTRACE_PARAMS,
} from "./stitchPotraceBitmap";

const DRAW_SCALE = 2; // canvas bitmap pixels per CSS pixel

// Accent for the draw tool (green = adding), distinct from erase red.
const ACCENT = "#16a34a";
const ACCENT_RGB = "22,163,74";

/**
 * Map Potrace bitmap pixel coords back to SVG viewBox coordinates.
 */
function scaleAndOffsetPath(d, scale, offsetX, offsetY) {
  if (!d) return d;
  let coordIdx = 0;
  return d.replace(
    /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
    (n) => {
      const isX = coordIdx % 2 === 0;
      coordIdx++;
      const scaled = parseFloat(n) * scale;
      return (scaled + (isX ? offsetX : offsetY)).toFixed(2);
    },
  );
}

/**
 * Pixel-by-pixel brush draw overlay for the Stitch Editor.
 *
 * The structural twin of StitchPixelEraseCanvas, but additive: it rasterizes the
 * selected layer onto a canvas, lets the user paint new pixels with a circular
 * brush, and on Apply unions the brushed pixels with the layer before
 * re-vectorizing via Potrace. The new path replaces the layer.
 */
const StitchDrawCanvas = forwardRef(({
  svgJoined,
  svgSplit,
  layerIdx,
  colors,
  layoutMetrics,
  pan,
  zoom,
  onApply,   // called with (layerIdx, newPathD) — keeps tool open
}, ref) => {
  const canvasRef  = useRef(null);
  const previewCanvasRef = useRef(null);
  const overlayRef = useRef(null);
  const layerSnapshotRef = useRef(null);
  const drawMaskRef = useRef(null);
  const strokePointsRef = useRef([]);
  const applyExitResolverRef = useRef(null);
  const hasDrawnRef = useRef(false);

  const [hasDrawn,     setHasDrawn]     = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [applyError,   setApplyError]   = useState(null);
  const [brushSize,    setBrushSize]    = useState(20); // CSS px radius

  // Resolve the toolbar's controls slot after mount (it commits in the same pass
  // as this overlay, so it isn't in the DOM during our first render).
  const [controlsSlot, setControlsSlot] = useState(null);
  useEffect(() => {
    let raf;
    const find = () => {
      const el = document.getElementById("stitch-erase-controls-slot");
      if (el) { setControlsSlot(el); return; }
      raf = requestAnimationFrame(find);
    };
    find();
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, []);

  // Cursor tracking
  const [cursorPos,    setCursorPos]    = useState(null);
  const isDrawingRef   = useRef(false);
  const lastPointRef   = useRef(null);

  const { dw, dh, vbX, vbY, vbW, designLeft, designTop } = layoutMetrics;
  const W = Math.round(dw * DRAW_SCALE);
  const H = Math.round(dh * DRAW_SCALE);

  const layerColor = colors?.[layerIdx] || "#000000";

  const captureSnapshot = useCallback(() => {
    const main = canvasRef.current;
    if (!main || main.width === 0) return;
    if (!layerSnapshotRef.current) {
      layerSnapshotRef.current = document.createElement("canvas");
    }
    const snap = layerSnapshotRef.current;
    snap.width = W;
    snap.height = H;
    snap.getContext("2d").drawImage(main, 0, 0);
  }, [W, H]);

  const clearDrawPreview = useCallback(() => {
    const preview = previewCanvasRef.current;
    if (!preview) return;
    preview.getContext("2d").clearRect(0, 0, preview.width, preview.height);
  }, []);

  // Offscreen canvas that accumulates the brushed-in pixels. They are only
  // merged into the layer on Apply.
  const ensureMaskCanvas = useCallback(() => {
    if (!drawMaskRef.current) drawMaskRef.current = document.createElement("canvas");
    const m = drawMaskRef.current;
    if (m.width !== W || m.height !== H) { m.width = W; m.height = H; }
    return m;
  }, [W, H]);

  const clearMask = useCallback(() => {
    const m = drawMaskRef.current;
    if (m) m.getContext("2d").clearRect(0, 0, m.width, m.height);
  }, []);

  // Paint the accumulated draw mask (plus an optional hover dab) in the accent
  // color onto the preview canvas. Unlike erase, additions are NOT clipped to
  // existing layer pixels — you can draw anywhere on the canvas.
  const renderPreview = useCallback((hoverPoint) => {
    const preview = previewCanvasRef.current;
    if (!preview) return;
    const pCtx = preview.getContext("2d");
    pCtx.clearRect(0, 0, W, H);
    pCtx.globalAlpha = 0.55;
    if (drawMaskRef.current) pCtx.drawImage(drawMaskRef.current, 0, 0);
    if (hoverPoint) {
      pCtx.fillStyle = `rgba(${ACCENT_RGB}, 1)`;
      pCtx.beginPath();
      pCtx.arc(hoverPoint.x, hoverPoint.y, brushSize * DRAW_SCALE, 0, Math.PI * 2);
      pCtx.fill();
    }
    pCtx.globalAlpha = 1;
  }, [brushSize, W, H]);

  // ── Rasterize current layer onto the canvas ───────────────────────────────

  const rasterizeLayer = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const source = svgSplit || svgJoined;
    if (!source?.children?.[layerIdx]) return;

    const layer = source.children[layerIdx];
    const viewBox = source.attributes?.viewBox || "0 0 1000 1000";

    const dStrings = collectLayerPathDs(layer);
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);

    if (dStrings.length === 0) {
      captureSnapshot();
      return;
    }

    const fillRule = layer?.attributes?.["fill-rule"] || layer?.attributes?.fillRule || "evenodd";
    // All subpaths MUST live in a single <path> with a compound d so the
    // evenodd winding rule creates holes correctly (letters with counters, etc.)
    const compoundD = dStrings.join(" ");
    const svgStr =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${W}" height="${H}">` +
      `<path d="${compoundD}" fill="${layerColor}" fill-rule="${fillRule}" />` +
      `</svg>`;

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, W, H);
      captureSnapshot();
    };
    img.onerror = () => {
      // Path2D fallback — combine all subpaths into one Path2D and fill
      // with the same evenodd rule so holes render correctly.
      const [vbx, vby, vbw, vbh] = viewBox.split(/\s+/).map(Number);
      ctx.save();
      ctx.translate(-vbx * (W / vbw), -vby * (H / vbh));
      ctx.scale(W / vbw, H / vbh);
      ctx.fillStyle = layerColor;
      try {
        const combinedPath = new Path2D(compoundD);
        ctx.fill(combinedPath, fillRule);
      } catch (_) { /* skip on unsupported path */ }
      ctx.restore();
      captureSnapshot();
    };

    try {
      const encoded = btoa(unescape(encodeURIComponent(svgStr)));
      img.src = "data:image/svg+xml;base64," + encoded;
    } catch (_) { img.onerror(); }
  }, [svgJoined, svgSplit, layerIdx, layerColor, W, H, captureSnapshot]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const preview = previewCanvasRef.current;
    if (!canvas) return;
    canvas.width  = W;
    canvas.height = H;
    if (preview) {
      preview.width = W;
      preview.height = H;
    }
    strokePointsRef.current = [];
    clearMask();
    clearDrawPreview();
    setHasDrawn(false);
    rasterizeLayer();
  }, [W, H, rasterizeLayer, clearDrawPreview, clearMask]);

  // ── Brush helpers ─────────────────────────────────────────────────────────

  const stampBrush = useCallback((ctx, cx, cy, radius) => {
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  const connectBrush = useCallback((ctx, x1, y1, cx, cy, radius) => {
    ctx.lineWidth = radius * 2;
    ctx.lineCap   = "round";
    ctx.lineJoin  = "round";
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(cx, cy);
    ctx.stroke();
  }, []);

  // Accumulate the brush stroke into the draw mask (non-destructive — the
  // pixels are merged into the layer on Apply).
  const drawPaint = useCallback((bmpX, bmpY, isStart) => {
    const mask = ensureMaskCanvas();
    const ctx = mask.getContext("2d");
    const scaledRadius = brushSize * DRAW_SCALE;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle   = `rgba(${ACCENT_RGB},1)`;
    ctx.strokeStyle = `rgba(${ACCENT_RGB},1)`;

    if (isStart || !lastPointRef.current) {
      stampBrush(ctx, bmpX, bmpY, scaledRadius);
    } else {
      connectBrush(ctx, lastPointRef.current.x, lastPointRef.current.y, bmpX, bmpY, scaledRadius);
      stampBrush(ctx, bmpX, bmpY, scaledRadius);
    }

    lastPointRef.current = { x: bmpX, y: bmpY };
  }, [brushSize, stampBrush, connectBrush, ensureMaskCanvas]);

  // ── Apply (Potrace) ───────────────────────────────────────────────────────

  const handleApplyRef = useRef(null);
  const applyStartRef = useRef(0);
  const applyEndTimerRef = useRef(null);

  const handleApply = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      applyExitResolverRef.current?.(false);
      applyExitResolverRef.current = null;
      return;
    }
    applyStartRef.current = Date.now();
    setIsProcessing(true);
    setApplyError(null);

    let bwCanvas;
    try {
      // Union the brushed-in pixels with the layer to get the final shape.
      const result = document.createElement("canvas");
      result.width = W;
      result.height = H;
      const rctx = result.getContext("2d");
      rctx.drawImage(canvas, 0, 0);
      if (drawMaskRef.current) {
        rctx.globalCompositeOperation = "source-over";
        rctx.drawImage(drawMaskRef.current, 0, 0);
      }
      const imageData = rctx.getImageData(0, 0, W, H);

      if (isCanvasEffectivelyEmpty(imageData)) {
        finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
          setHasDrawn(false);
          onApply?.(layerIdx, "");
          applyExitResolverRef.current?.(true);
          applyExitResolverRef.current = null;
        });
        return;
      }

      bwCanvas = buildPotraceBitmapCanvas(imageData, W, H);
    } catch (err) {
      console.error("[StitchDrawCanvas] Canvas read error:", err);
      setIsProcessing(false);
      setApplyError("Could not read canvas. Try again.");
      applyExitResolverRef.current?.(false);
      applyExitResolverRef.current = null;
      return;
    }

    bwCanvas.toBlob((blob) => {
      if (!blob) {
        setIsProcessing(false);
        setApplyError("Failed to encode. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
        return;
      }
      const blobUrl   = URL.createObjectURL(blob);
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        setIsProcessing(false);
        setApplyError("Vectorization timed out. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
      }, 15000);

      const trace = new Potrace.Potrace();
      trace.setParameters(PIXEL_ERASE_POTRACE_PARAMS);

      trace.loadImage(blobUrl, (loadErr) => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(blobUrl);
        if (loadErr) {
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
          return;
        }
        try {
          const pathTag = trace.getPathTag();
          const match = pathTag?.match(/d="([^"]+)"/);
          if (!match) {
            finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
              setHasDrawn(false);
              onApply?.(layerIdx, "");
              applyExitResolverRef.current?.(true);
              applyExitResolverRef.current = null;
            });
            return;
          }
          const newPathD = scaleAndOffsetPath(match[1], vbW / W, vbX, vbY);
          finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
            setHasDrawn(false);
            onApply?.(layerIdx, newPathD);
            applyExitResolverRef.current?.(true);
            applyExitResolverRef.current = null;
          });
        } catch (err) {
          console.error("[StitchDrawCanvas] Potrace error:", err);
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
        }
      });
    }, "image/png");
  }, [W, H, vbX, vbY, vbW, layerIdx, onApply]);

  // Keep ref in sync so pointer handlers can call it without a stale closure
  useEffect(() => { handleApplyRef.current = handleApply; }, [handleApply]);
  useEffect(() => () => {
    if (applyEndTimerRef.current) clearTimeout(applyEndTimerRef.current);
  }, []);

  // Drop the pending brush strokes — the layer is untouched until Apply.
  const handleDiscard = useCallback(() => {
    if (isProcessing) return;
    strokePointsRef.current = [];
    clearMask();
    clearDrawPreview();
    setHasDrawn(false);
    setApplyError(null);
  }, [isProcessing, clearMask, clearDrawPreview]);

  useImperativeHandle(ref, () => ({
    hasWorkToApply: () => hasDrawnRef.current,
    applyAndExit: () => new Promise((resolve) => {
      if (isProcessing || !hasDrawnRef.current) {
        resolve(false);
        return;
      }
      applyExitResolverRef.current = resolve;
      handleApply();
    }),
  }), [isProcessing, handleApply]);

  // ── Pointer handlers ──────────────────────────────────────────────────────

  const clientToBmp = useCallback((e) => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    const rect = overlay.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    // Map screen coords → overlay local space (undo CSS scale/zoom on the transform).
    const cssX = ((e.clientX - rect.left) / rect.width) * dw;
    const cssY = ((e.clientY - rect.top) / rect.height) * dh;
    return { x: cssX * DRAW_SCALE, y: cssY * DRAW_SCALE, cssX, cssY };
  }, [dw, dh]);

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0 || isProcessing) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = clientToBmp(e);
    if (!pt) return;
    isDrawingRef.current = true;
    lastPointRef.current = null;
    drawPaint(pt.x, pt.y, true);
    renderPreview(null);
    setHasDrawn(true);
  }, [clientToBmp, drawPaint, renderPreview, isProcessing]);

  const onPointerMove = useCallback((e) => {
    const pt = clientToBmp(e);
    if (!pt) return;
    setCursorPos({ x: pt.cssX, y: pt.cssY });
    if (!isDrawingRef.current) {
      renderPreview({ x: pt.x, y: pt.y });
      return;
    }
    drawPaint(pt.x, pt.y, false);
    renderPreview(null);
  }, [clientToBmp, drawPaint, renderPreview]);

  useEffect(() => { hasDrawnRef.current = hasDrawn; }, [hasDrawn]);

  const onPointerUp = useCallback(() => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
    // Keep the accumulated strokes visible until Apply.
    renderPreview(null);
  }, [renderPreview]);

  const onPointerLeave = useCallback(() => {
    isDrawingRef.current = false;
    lastPointRef.current = null;
    setCursorPos(null);
    renderPreview(null);
  }, [renderPreview]);

  const onPointerEnter = useCallback((e) => {
    const pt = clientToBmp(e);
    if (!pt) return;
    setCursorPos({ x: pt.cssX, y: pt.cssY });
    if (!isDrawingRef.current) {
      renderPreview({ x: pt.x, y: pt.y });
    }
  }, [clientToBmp, renderPreview]);

  // Re-draw hover preview when brush size changes.
  useEffect(() => {
    if (isDrawingRef.current || !cursorPos) return;
    const overlay = overlayRef.current;
    if (!overlay) return;
    const bmpX = cursorPos.x * DRAW_SCALE;
    const bmpY = cursorPos.y * DRAW_SCALE;
    renderPreview({ x: bmpX, y: bmpY });
  }, [brushSize, cursorPos, renderPreview]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`@keyframes stitch-ai-spin { to { transform: rotate(360deg); } }`}</style>
      {/* Raster brush draw overlay */}
      <div
        ref={overlayRef}
        style={{
          position: "absolute",
          top: 0, left: 0,
          width:  `${dw}px`,
          height: `${dh}px`,
          transformOrigin: "0 0",
          transform: `translate(${pan.x + designLeft * zoom}px, ${pan.y + designTop * zoom}px) scale(${zoom})`,
          cursor: isProcessing ? "wait" : "none",
          zIndex: 50,
          touchAction: "none",
          userSelect: "none",
          pointerEvents: isProcessing ? "none" : "auto",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerEnter={onPointerEnter}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block" }}
        />
        <canvas
          ref={previewCanvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        />

        {/* Brush outline — stroke width stays ~constant on screen at any zoom */}
        {cursorPos && !isProcessing && (
          <div
            style={{
              position: "absolute",
              left: cursorPos.x - brushSize,
              top: cursorPos.y - brushSize,
              width: brushSize * 2,
              height: brushSize * 2,
              borderRadius: "50%",
              border: `${Math.max(1, 1.5 / zoom)}px solid rgba(${ACCENT_RGB},0.9)`,
              boxShadow: `0 0 0 ${Math.max(1, 1 / zoom)}px rgba(255,255,255,0.8)`,
              pointerEvents: "none",
              boxSizing: "border-box",
            }}
          />
        )}

        {isProcessing && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              background: "transparent",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                border: `3px solid rgba(${ACCENT_RGB},0.2)`,
                borderTop: `3px solid ${ACCENT}`,
                animation: "stitch-ai-spin 0.7s linear infinite",
                filter: "drop-shadow(0 1px 2px rgba(255,255,255,0.85))",
              }}
            />
            <span style={{
              fontSize: "11px",
              fontWeight: 600,
              color: ACCENT,
              fontFamily: "Clash Grotesk, sans-serif",
              textShadow: "0 1px 3px rgba(255,255,255,0.95), 0 0 6px rgba(255,255,255,0.75)",
            }}>
              Applying draw…
            </span>
          </div>
        )}
      </div>

      {/* Controls portaled into the shared erase-controls slot */}
      {controlsSlot && createPortal(
          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "10px" }}>
            {/* Brush size slider */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "11px", color: "#888", fontFamily: "Clash Grotesk, sans-serif", whiteSpace: "nowrap" }}>
                ◌ Brush
              </span>
              <input
                type="range"
                min={4}
                max={60}
                value={brushSize}
                disabled={isProcessing}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                style={{
                  flex: 1,
                  accentColor: ACCENT,
                  cursor: isProcessing ? "not-allowed" : "pointer",
                  opacity: isProcessing ? 0.5 : 1,
                }}
              />
              <span style={{ fontSize: "11px", color: "#555", fontFamily: "Clash Grotesk, sans-serif", minWidth: "20px", textAlign: "right" }}>
                {brushSize}
              </span>
            </div>

            {!isProcessing && !applyError && (
              <span style={{
                fontSize: "11px",
                fontWeight: 500,
                color: ACCENT,
                fontFamily: "Clash Grotesk, sans-serif",
                lineHeight: 1.35,
              }}>
                {hasDrawn ? "Keep drawing, then Apply — or Discard" : "Brush over the area to add"}
              </span>
            )}

            {isProcessing && (
              <span style={{
                fontSize: "11px",
                color: ACCENT,
                fontFamily: "Clash Grotesk, sans-serif",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <span style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  border: `2px solid rgba(${ACCENT_RGB},0.25)`,
                  borderTop: `2px solid ${ACCENT}`,
                  animation: "stitch-ai-spin 0.7s linear infinite",
                  display: "inline-block",
                  flexShrink: 0,
                }} />
                Applying…
              </span>
            )}
            {applyError && (
              <span style={{ fontSize: "11px", color: "#ef4444", fontFamily: "Clash Grotesk, sans-serif" }}>
                {applyError}
              </span>
            )}

            <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDiscard}
              disabled={isProcessing || !hasDrawn}
              style={{
                flex: 1,
                border: "1px solid rgba(0,0,0,0.1)",
                background: "#fff",
                color: "#888",
                fontSize: "11px",
                fontWeight: 600,
                padding: "7px 12px",
                borderRadius: "8px",
                cursor: (isProcessing || !hasDrawn) ? "not-allowed" : "pointer",
                fontFamily: "Clash Grotesk, sans-serif",
                opacity: (isProcessing || !hasDrawn) ? 0.5 : 1,
              }}
            >
              Discard
            </button>
            <button
              onClick={() => handleApplyRef.current?.()}
              disabled={isProcessing || !hasDrawn}
              style={{
                flex: 1,
                border: "none",
                background: (isProcessing || !hasDrawn) ? "#f3f4f6" : `linear-gradient(135deg, ${ACCENT}, #15803d)`,
                color: (isProcessing || !hasDrawn) ? "#9ca3af" : "#fff",
                fontSize: "11px",
                fontWeight: 700,
                padding: "7px 12px",
                borderRadius: "8px",
                cursor: (isProcessing || !hasDrawn) ? "not-allowed" : "pointer",
                fontFamily: "Clash Grotesk, sans-serif",
              }}
            >
              Apply
            </button>
            </div>
          </div>,
          controlsSlot,
        )}
    </>
  );
});

export default StitchDrawCanvas;
